import React from "react";
import { Users, FileText, Zap, Layers, ShieldCheck, UploadCloud } from "lucide-react";
import { motion } from "framer-motion";

const Home = ({ onNavigate }) => {
  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Your AI-Powered Recruitment Hub
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Intelligent resume filtering, role management, and automated HR workflows — in one place.
          </p>
        </motion.div>

        {/* ================= AGENT CARDS ================= */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 pl-80 gap-8"
        >
          {/* Recruiter Agent */}
          <AnimatedCard onClick={() => onNavigate("recruiter")}>
            <Users className="w-14 h-14 text-orange-600 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Recruiter Agent</h3>
            <p className="text-gray-600">
              AI-driven resume filtering, ranking, and hiring recommendations.
            </p>
          </AnimatedCard>

          {/* Inventory Agent */}
          <AnimatedCard onClick={() => onNavigate("creyente")}>
            <FileText className="w-14 h-14 text-orange-600 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Inventory Agent</h3>
            <p className="text-gray-600">
              Track and manage equipment issued to employees with intelligent logging.
            </p>
          </AnimatedCard>

         
        </motion.div>

        {/* ================= FEATURES SECTION ================= */}
        <div className="mt-20 bg-white/80 backdrop-blur-xl rounded-2xl p-12 shadow-xl border border-gray-200">
          <h2 className="text-3xl font-semibold text-gray-900 mb-10 text-center">
            Powerful Features for Smarter HR
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">

            <Feature
              icon={<Zap className="text-orange-600" size={28} />}
              title="AI-Powered Resume Filtering"
              desc="Instantly evaluate, score, and shortlist the best candidates."
            />

            <Feature
              icon={<Layers className="text-orange-600" size={28} />}
              title="Dynamic Role Management"
              desc="Add, delete, and organize job roles with one click."
            />

            <Feature
              icon={<UploadCloud className="text-orange-600" size={28} />}
              title="Bulk Resume Uploads"
              desc="Effortlessly upload multiple PDFs at once with automated parsing."
            />

            <Feature
              icon={<ShieldCheck className="text-orange-600" size={28} />}
              title="Secure Cloud Storage"
              desc="All resumes stored safely using encrypted AWS S3 storage."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

/* ======================= REUSABLE COMPONENTS ======================= */

const AnimatedCard = ({ children, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.04, y: -4 }}
      whileTap={{ scale: 0.98 }}
      className="
        bg-white/70 backdrop-blur-xl 
        rounded-2xl p-10 shadow-md border border-gray-200 
        hover:shadow-xl text-left transition-all
      "
    >
      {children}
    </motion.button>
  );
};

const Feature = ({ icon, title, desc }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="flex items-start gap-4"
    >
      <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-1 text-lg">{title}</h4>
        <p className="text-gray-600 text-sm">{desc}</p>
      </div>
    </motion.div>
  );
};
